import { observer, use$ } from '@legendapp/state/react';
import { Card } from '@mantine/core';
import { useCallback } from 'react';
import { PATTERN_MAP } from '../../../constants';
import { useManualBet } from '../../../hooks';
import { gameState$, manualBetState$, towersState$ } from '../../../state';
import { BrickIcon } from '../BrickIcon';
import styles from './ManualBetTowersGridBrick.module.css';

export type ManualBetTowersGridBrickProps = {
  row: number;
  column: number;
};

export const ManualBetTowersGridBrick = observer(
  ({ row, column }: ManualBetTowersGridBrickProps) => {
    const { handleManualBet } = useManualBet();

    const bricks = use$(towersState$.bricks);

    const pattern = gameState$.pattern.get();

    const { rows, cols } = PATTERN_MAP[pattern];

    const brick = bricks[row]?.[column];

    // Determine the currently active row – the next row the player is allowed to pick.
    // Since rows are pushed from bottom (index 0) upwards, the active row index
    // corresponds to the current number of picked rows (bricks.length).
    const activeRowIndex = bricks.length;

    const isInActiveRow = row === activeRowIndex;

    const isBetting = use$(manualBetState$.isBetting);

    const handleOnClick = useCallback(() => {
      if (isBetting && isInActiveRow) {
        handleManualBet(column);
      }
    }, [isBetting, isInActiveRow, column, handleManualBet]);

    return (
      <Card
        className={styles.brick}
        data-active={isInActiveRow}
        data-betting={isBetting}
        data-brick={brick?.type === 'BRICK'}
        data-column={column}
        data-mine={brick?.type === 'MINE'}
        data-picked={brick?.isPicked}
        data-row={row}
        data-test-id="manual-bet-brick"
        data-unknown={brick?.type === 'UNKNOWN'}
        onClick={handleOnClick}
        p={0}
        style={{
          aspectRatio: rows / cols,
        }}
        withBorder
      >
        {brick ? (
          <BrickIcon data-test-id="manual-bet-brick-icon" type={brick.type} />
        ) : null}
      </Card>
    );
  }
);
